<template>
  <div class="ml-3">
    <v-card>
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text class="pa-0">
        <v-overlay absolute v-if="loading">
          <v-progress-circular
            indeterminate
            :size="50"
            :width="5"
            color="primary"
          >
          </v-progress-circular>
        </v-overlay>

        <v-row>
          <v-col>
            <v-text-field
              v-model="filter.search"
              :label="$t('Search') + '...'"
              prepend-icon="mdi-magnify"
              class="ml-2"
              v-debounce:300ms="changeFilter"
            ></v-text-field>
          </v-col>
          <v-col>
            <BaseDatePicker
              :label="$t('From')"
              clearable
              class="ml-2"
              v-model="filter.from"
              @change="changeFilter"
            />
          </v-col>
          <v-col>
            <BaseDatePicker
              :label="$t('To')"
              clearable
              class="ml-2"
              v-model="filter.to"
              @change="changeFilter"
            />
          </v-col>
          <v-spacer></v-spacer>
          <v-col class="text-xs-right">
            <v-btn
              small
              color="success"
              @click.prevent="onRestoreSelected"
              :loading="restoreLoading"
              class="mt-4"
            >
              <v-icon left>mdi-history</v-icon>
              {{ $t("Restore") }}
            </v-btn>
            <v-btn
              small
              color="red"
              @click.prevent="onDeleteSelected"
              :loading="deleteLoading"
              class="mt-4"
            >
              <v-icon left>mdi-delete</v-icon>
              {{ $t("Delete") }}
            </v-btn>
          </v-col>
        </v-row>

        <div class="text-center blue-grey lighten-4 pt-2 pb-2" v-if="selectAll">
          {{ $t("Selected") }}: {{ selectedItemCount() }} {{ $t("Documents") }}
        </div>

        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">
                <v-checkbox
                  v-model="selectAll"
                  @change="selectAllItemsInPage"
                  dense
                ></v-checkbox>
              </th>
              <th class="text-left" @click="onTableHeadClicked('ContentType')">
                <h4 href="javascript:void(0)" style="cursor: pointer">
                  {{ this.$t("ContentType") }}
                  <span
                    v-if="
                      'ContentType' === paginationSetting.orderBy &&
                      !paginationSetting.desc
                    "
                    >↓</span
                  >
                  <span
                    v-if="
                      'ContentType' === paginationSetting.orderBy &&
                      paginationSetting.desc
                    "
                    >↑</span
                  >
                </h4>
              </th>
              <th class="text-left" @click="onTableHeadClicked('UserName')">
                <h4 href="javascript:void(0)" style="cursor: pointer">
                  {{ this.$t("User") }}
                  <span
                    v-if="
                      'UserName' === paginationSetting.orderBy &&
                      !paginationSetting.desc
                    "
                    >↓</span
                  >
                  <span
                    v-if="
                      'UserName' === paginationSetting.orderBy &&
                      paginationSetting.desc
                    "
                    >↑</span
                  >
                </h4>
              </th>
              <th class="text-left" @click="onTableHeadClicked('DelDate')">
                <h4 style="cursor: pointer">
                  {{ this.$t("Date") }}
                  <span
                    v-if="
                      'DelDate' === paginationSetting.orderBy &&
                      !paginationSetting.desc
                    "
                    >↓</span
                  >
                  <span
                    v-if="
                      'DelDate' === paginationSetting.orderBy &&
                      paginationSetting.desc
                    "
                    >↑</span
                  >
                </h4>
              </th>
              <th
                class="text-left"
                @click="onTableHeadClicked('VersionNumber')"
              >
                <h4 style="cursor: pointer">
                  {{ this.$t("Version") }}
                  <span
                    v-if="
                      'VersionNumber' === paginationSetting.orderBy &&
                      !paginationSetting.desc
                    "
                    >↓</span
                  >
                  <span
                    v-if="
                      'VersionNumber' === paginationSetting.orderBy &&
                      paginationSetting.desc
                    "
                    >↑</span
                  >
                </h4>
              </th>
              <th class="text-left" @click="onTableHeadClicked('IpAddress')">
                <h4 style="cursor: pointer">
                  {{ this.$t("IP") }}
                  <span
                    v-if="
                      'IpAddress' === paginationSetting.orderBy &&
                      !paginationSetting.desc
                    "
                    >↓</span
                  >
                  <span
                    v-if="
                      'IpAddress' === paginationSetting.orderBy &&
                      paginationSetting.desc
                    "
                    >↑</span
                  >
                </h4>
              </th>
              <th class="text-left"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="dataSource.length === 0">
              <td colspan="9999" class="text-center table-no-data">{{ this.$t("NoData") }}</td>
            </tr>
            <tr
              v-for="(column, index) in dataSource"
              :key="index"
              @click="toggleRowSelect(column)"
            >
              <td>
                <v-checkbox
                  v-model="column.IsSelected"
                  @change="selectThisItem"
                  dense
                  hide-details
                  class="mt-0"
                  @click.stop
                ></v-checkbox>
              </td>
              <td>{{ column.ContentType }}</td>
              <td>{{ column.UserName }}</td>
              <td>{{ calculateCellValue(column.DelDate) }}</td>
              <td>{{ column.VersionNumber }}</td>
              <td>{{ column.IpAddress }}</td>
              <td>
                <template>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <a
                        v-on="on"
                        @click.prevent="openModal(column.Id)"
                        class="px-2"
                        @click.stop
                      >
                        <v-icon color="grey">mdi-information</v-icon>
                      </a>
                    </template>
                    <span>{{ $t("Detail") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        color="blue"
                        :link="true"
                        target="_blank"
                        :to="getRoute(column.Id)"
                        @click.stop
                      >
                        <v-icon color="blue">mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("View") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <a
                        v-on="on"
                        @click.prevent="restoreItem(column.Id)"
                        class="px-2"
                      >
                        <v-icon color="success">mdi-history</v-icon>
                      </a>
                    </template>
                    <span>{{ $t("Restore") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <a
                        v-on="on"
                        @click.prevent="deleteItem(column.Id)"
                        class="px-2"
                      >
                        <v-icon color="red">mdi-delete</v-icon>
                      </a>
                    </template>
                    <span>{{ $t("Delete") }}</span>
                  </v-tooltip>
                </template>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <PaginationComponent
          :ref="paginationComponentKey"
          :totalPages="totalPages"
          :totalItems="totalItems"
          @on-page-change="getData"
        >
        </PaginationComponent>
      </v-card-text>
    </v-card>
    <v-dialog v-model="isModalOpen" max-width="450px">
      <v-card>
        <v-card-title>{{ this.$t("Detail") }}</v-card-title>
        <v-simple-table dense class="ml-2 mb-4 mt-3">
          <thead>
            <tr>
              <th
                class="text-left"
                v-for="field in modalData.fields"
                :key="field.title"
              >
                {{ field.title }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                class="text-left"
                v-for="field in modalData.fields"
                :key="field.value"
              >
                {{ field.value }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-card-actions class="d-flex justify-center">
          <v-btn class="ml-4" color="primary" @click="closeModal">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DocumentService from "@/services/DocumentService";
import SearchType from "@/ecm/variables/SearchType";
import Utils from "@/ecm/common/Utils";
import PaginationSetting from "../../../ecm/models/PaginationSetting";
import PaginationComponent from "@/components/base/PaginationComponent.vue";

export default {
  components: {
    PaginationComponent,
  },
  data() {
    return {
      deleteLoading: false,
      restoreLoading: false,
      title: this.$t("DeletedDocuments"),
      dataSource: [],
      paginationSetting: new PaginationSetting(),
      loading: false,
      totalPages: 1,
      paginationComponentKey: "paginationComponentKey",
      totalItems: 1,
      selectAll: false,
      isModalOpen: false,
      modalData: {
        fields: [],
      },
      filter: {
        from: null,
        to: null,
        search: null,
      },
    };
  },
  created(){
    this.$nextTick(() => {
      this.paginationComponentInstance.reset(true);
    });
  },
  computed: {
    paginationComponentInstance() {
      return this.$refs[this.paginationComponentKey];
    },
  },
  methods: {
    openModal(id) {
      this.loading = true;
      DocumentService.get(id)
        .then((data) => {
          let count = 0;
          let fieldsToShow = 3;
          if (data.Fields.length < 3) {
            fieldsToShow = data.Fields.length;
          }
          for (let i = 0; i < data.Fields.length && count < fieldsToShow; i++) {
            if (data.Fields[i].Value !== "") {
              let field = {
                title: data.Fields[i].Title,
                value: data.Fields[i].Value,
              };
              this.modalData.fields.push(field);
              count++;
            }
          }
          this.modalData.title = this.$t("Documents");
          this.loading = false;
          this.isModalOpen = true; // Abre el modal
        })
        .catch((error) => {
          this.loading = false;
          this.closeModal();
        });
    },
    closeModal() {
      this.isModalOpen = false; // Cierra el modal
      this.modalData.fields = [];
    },
    getRoute(id) {
      let pattern = {
        searchPattern: id,
        searchAllVersions: true,
        allowDeleted: true,
      };
      let route = "/search/";
      route += `${SearchType.DocumentIDs}/${encodeURIComponent(
        JSON.stringify(pattern)
      )}`;
      return route;
    },
    getData(paginationOptions) {
      
      this.loading = true;
      this.paginationSetting.filter = this.filter.search;
      this.paginationSetting.dateFrom = Utils.date.toIsoFromUserFormat(
        this.filter.from
      );
      this.paginationSetting.dateTo = Utils.date.toIsoFromUserFormat(
        this.filter.to
      );
      if (this.paginationSetting.page > this.totalPages) {
        this.paginationSetting.page = this.totalPages;
      }
      if (paginationOptions && paginationOptions.page) {
        this.paginationSetting.page = paginationOptions.page;
      }
      if (paginationOptions && paginationOptions.pageSize)
        this.paginationSetting.pageSize = paginationOptions.pageSize;

      DocumentService.getDeletedGrid(this.paginationSetting).then((data) => {
        this.totalPages = Math.ceil(
          data.Count / this.paginationSetting.pageSize
        );
        if(this.totalPages === 0)
          this.totalPages = 1;
        this.totalItems = data.Count;
        this.dataSource = data.Result;
        if (this.dataSource.length === 0 && this.paginationSetting.page > 1) {
          // If it's empty, go back to the previous page
          this.$refs.paginationComponentKey.changePage(this.paginationSetting.page - 1);
        }
        this.loading = false;
      });
    },
    deleteItem(id) {
      this.$confirm(this.$t("DeleteDocumentsPermanentlyConfirmation"), {
        title: this.$t("Delete"),
        icon: "mdi-alert",
        color: "red",
        buttonTrueText: this.$t("Delete"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res == 1) {
          DocumentService.removeDeleted([id]).then((result) => {
            this.selectAll = false;
            this.getData();
          });
        }
      });
    },
    restoreItem(id) {
      this.$confirm(this.$t("DocumentsRestoreConfirmation"), {
        title: this.$t("Restore"),
        icon: "mdi-alert",
        color: "warning",
        buttonTrueText: this.$t("Restore"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res === 1) {
          DocumentService.restoreDeleted([id]).then((result) => {
            this.getData();
          });
        }
      });
    },
    onRestoreSelected() {
      let selectedItems = this.dataSource
        .filter((x) => x.IsSelected)
        .map((t) => t.Id);
      if (selectedItems.length === 0) {
        Utils.toast.warning(this.$t("ItemsSelectionError"));
        return;
      }

      this.$confirm(this.$t("DocumentsRestoreConfirmation"), {
        title: this.$t("Restore"),
        icon: "mdi-alert",
        color: "warning",
        buttonTrueText: this.$t("Restore"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res === 1) {
          this.restoreLoading = true;
          DocumentService.restoreDeleted(selectedItems)
            .then((result) => {
              this.getData();
            })
            .catch((error) => {
              // error
            })
            .then(() => {
              //always
              this.restoreLoading = false;
            });
        }
      });
    },
    onDeleteSelected() {
      let selectedItems = this.dataSource
        .filter((x) => x.IsSelected)
        .map((t) => t.Id);
      if (selectedItems.length === 0) {
        Utils.toast.warning(this.$t("ItemsSelectionError"));
        this.selectAll = false;
        return;
      }

      this.$confirm(this.$t("DeleteDocumentsPermanentlyConfirmation"), {
        title: this.$t("Delete"),
        icon: "mdi-alert",
        color: "red",
        buttonTrueText: this.$t("Delete"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res === 1) {
          this.deleteLoading = true;
          DocumentService.removeDeleted(selectedItems)
            .then((result) => {
              this.getData();
            })
            .catch((error) => {
              // error
            })
            .then(() => {
              //always
              this.selectAll = false;
              this.deleteLoading = false;
            });
        }
      });
    },
    calculateCellValue(delDate) {
      return Utils.date.toUserFormatFromIso(delDate);
    },
    onTableHeadClicked(column) {
      if (
        this.paginationSetting.orderBy !== column ||
        this.paginationSetting.orderBy === ""
      ) {
        this.paginationSetting.desc = false;
      } else {
        this.paginationSetting.desc = !this.paginationSetting.desc;
      }

      this.paginationSetting.orderBy = column;
      this.getData();
    },
    selectAllItemsInPage() {
      this.dataSource.forEach(
        (element) => (element.IsSelected = this.selectAll)
      );
    },
    selectThisItem() {
      this.selectAll = this.dataSource.every((element) => element.IsSelected);
    },
    selectedItemCount() {
      return this.dataSource.filter((x) => x.IsSelected).length;
    },
    selectEveryItem() {
      this.ignorePagingOnDelete = true;
    },
    toggleRowSelect(column) {
      column.IsSelected = !column.IsSelected;
      this.$forceUpdate();
      this.selectAll = this.dataSource.every((element) => element.IsSelected);
    },
    clearSelection() {
      this.ignorePagingOnDelete = false;
      this.selectAll = false;
      this.dataSource.forEach((element) => (element.IsSelected = false));
    },
    changeFilter() {
      this.paginationComponentInstance.reset();
    },
  },
};
</script>
